import React, { MouseEvent, MouseEventHandler } from "react";

import { useGetLinkComponentAndProps } from "../../../hooks/link";
import {
  typographyBodyLineHeightMap,
  typographyBodySizeMap
} from "../../../styles/typography/typography";
import {
  ButtonLoadingStateProvider,
  getBorderStyleProps,
  getMiscStyleProps,
  getPaddingStyleProps,
  SharedButtonProps
} from "../Button";
import { sizeToTypographyBodySizeMap } from "../Button/utils/sizeToTypographyBodySizeMap";
import useButtonColorStyleProps from "../Button/utils/useButtonColorStyleProps";

import type { LocationShape } from "../../../types";

export type ButtonLinkProps = {
  to: string | LocationShape;
  newTab?: boolean;
  replace?: boolean;
  onClick?: MouseEventHandler;
} & SharedButtonProps;

export default function ButtonLink({
  to,
  newTab,
  replace = false,
  onClick,
  children,
  tone = "primary",
  fill = "filled",
  size = "standard",
  disabled = false,
  loading = false,
  width = ["100%", undefined],
  fillHeight = false,
  accessibilityLabel
}: ButtonLinkProps) {
  const miscStyleProps = getMiscStyleProps({
    width,
    fillHeight,
    accessibilityLabel,
    disabled,
    loading
  });

  const colorStyleProps = useButtonColorStyleProps({
    fill,
    tone,
    disabled
  });

  const borderStyleProps = getBorderStyleProps(fill);

  const paddingStyleProps = getPaddingStyleProps(size);

  const typographyBodySize = sizeToTypographyBodySizeMap[size];

  const { Component, linkProps } = useGetLinkComponentAndProps({
    to,
    disabled: disabled || loading,
    newTab,
    replace
  });

  return (
    <Component
      onClick={(e: MouseEvent) => {
        !disabled && onClick && onClick(e);
      }}
      {...miscStyleProps}
      {...colorStyleProps}
      {...borderStyleProps}
      {...paddingStyleProps}
      {...typographyBodySizeMap[typographyBodySize]}
      {...typographyBodyLineHeightMap.large}
      {...linkProps}
    >
      <ButtonLoadingStateProvider loading={loading} size={size}>
        {children}
      </ButtonLoadingStateProvider>
    </Component>
  );
}
